import { useEffect, useState } from "react";
import { api, fmtMoney, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, RefreshCw, Wand2, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function MultiCurrency() {
    const [exposure, setExposure] = useState(null);
    const [loading, setLoading] = useState(true);
    const [asOf, setAsOf] = useState(today());
    const [rates, setRates] = useState({});
    const [preview, setPreview] = useState(null);
    const [posting, setPosting] = useState(false);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/fx-exposure");
        setExposure(data);
        // Init rates with the average book rate per currency
        const init = {};
        (data.exposures || []).forEach((e) => {
            const totalBal = (e.ar_amount || 0) + (e.ap_amount || 0);
            const totalBase = (e.ar_base_at_book || 0) + (e.ap_base_at_book || 0);
            init[e.currency] = totalBal > 0 ? +(totalBase / totalBal).toFixed(4) : 1;
        });
        setRates(init);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const runPreview = async (post = false) => {
        setPosting(true);
        try {
            const { data } = await api.post("/fx-revalue", { as_of_date: asOf, rates });
            setPreview(data);
            if (post && data.posted) {
                toast.success(`FX revaluation posted. Net ${data.net_gain_loss > 0 ? "gain" : "loss"} ${fmtMoney(Math.abs(data.net_gain_loss), exposure?.base_currency)}`);
                await load();
            } else if (post) {
                toast.info(data.message || "No revaluation needed");
            }
        } catch (e) {
            toast.error("Failed to revalue: " + (e.response?.data?.detail || e.message));
        } finally {
            setPosting(false);
        }
    };

    return (
        <>
            <PageHeader
                kicker="ACCOUNTING"
                title="Multi-Currency"
                subtitle="Track foreign-currency exposure, set period-end rates, and post realized & unrealized FX gains/losses."
                actions={<Button variant="outline" onClick={load} data-testid="fx-refresh"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>}
            />
            <PageBody>
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-6">
                        {/* Exposure cards */}
                        <div>
                            <div className="text-[10.5px] tracking-[0.16em] font-semibold text-muted-foreground mb-2">OPEN FX EXPOSURE — BASE: {exposure?.base_currency}</div>
                            {(exposure?.exposures || []).length === 0 ? (
                                <div className="border border-border rounded-md p-8 bg-card text-center text-muted-foreground">
                                    <Globe className="w-6 h-6 mx-auto mb-2 opacity-50" />
                                    No foreign-currency open balances. All AR & AP are in base currency.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {exposure.exposures.map((e) => (
                                        <div key={e.currency} className="border border-border rounded-md p-4 bg-card" data-testid={`fx-card-${e.currency}`}>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] tracking-[0.16em] font-semibold text-[#a68a56]">{e.currency}</span>
                                                </div>
                                                <span className="text-[10.5px] text-muted-foreground">avg book {((e.ar_base_at_book + e.ap_base_at_book) / Math.max(e.ar_amount + e.ap_amount, 0.01)).toFixed(4)}</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <div className="text-[10.5px] text-muted-foreground tracking-wide">AR (open)</div>
                                                    <div className="font-semibold">{fmtMoney(e.ar_amount, e.currency)}</div>
                                                    <div className="text-[11.5px] text-muted-foreground">@ book = {fmtMoney(e.ar_base_at_book, exposure.base_currency)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-[10.5px] text-muted-foreground tracking-wide">AP (open)</div>
                                                    <div className="font-semibold">{fmtMoney(e.ap_amount, e.currency)}</div>
                                                    <div className="text-[11.5px] text-muted-foreground">@ book = {fmtMoney(e.ap_base_at_book, exposure.base_currency)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Revaluation form */}
                        {(exposure?.exposures || []).length > 0 && (
                            <div className="border border-border rounded-md p-5 bg-card">
                                <div className="flex items-center gap-2 mb-1">
                                    <Wand2 className="w-4 h-4 text-[#a68a56]" />
                                    <h3 className="text-base font-semibold">Period-end FX revaluation</h3>
                                </div>
                                <p className="text-[12.5px] text-muted-foreground mb-4">
                                    Enter the closing rates per foreign currency. We will revalue all open AR & AP balances and post a single FX adjustment journal with realized/unrealized gain or loss to the appropriate accounts.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label className="text-xs">As-of date</Label>
                                        <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="fx-asof" />
                                    </div>
                                    {exposure.exposures.map((e) => (
                                        <div key={e.currency}>
                                            <Label className="text-xs">{e.currency} → {exposure.base_currency} rate</Label>
                                            <Input
                                                type="number"
                                                step="0.0001"
                                                value={rates[e.currency] ?? ""}
                                                onChange={(ev) => setRates((r) => ({ ...r, [e.currency]: parseFloat(ev.target.value) || 0 }))}
                                                data-testid={`fx-rate-${e.currency}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                    <Button variant="outline" onClick={() => runPreview(false)} disabled={posting} data-testid="fx-preview">
                                        Preview
                                    </Button>
                                    <Button onClick={() => runPreview(true)} disabled={posting} data-testid="fx-post">
                                        {posting ? "Posting…" : "Post FX Revaluation"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Preview / posted result */}
                        {preview && (
                            <div className="border border-border rounded-md p-5 bg-card">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-base font-semibold flex items-center gap-2">
                                        {preview.net_gain_loss > 0 ? <TrendingUp className="w-4 h-4 text-[#1f6c47]" /> : preview.net_gain_loss < 0 ? <TrendingDown className="w-4 h-4 text-[#b4233a]" /> : <AlertCircle className="w-4 h-4 text-muted-foreground" />}
                                        {preview.posted ? "Posted Revaluation" : "Preview"}
                                    </h3>
                                    <span className={`text-lg font-semibold ${preview.net_gain_loss > 0 ? "text-[#1f6c47]" : preview.net_gain_loss < 0 ? "text-[#b4233a]" : ""}`}>
                                        Net {preview.net_gain_loss > 0 ? "gain" : "loss"}: {fmtMoney(Math.abs(preview.net_gain_loss || 0), exposure?.base_currency)}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                                    <Stat label="AR delta" value={fmtMoney(preview.ar_delta || 0, exposure?.base_currency)} accent={preview.ar_delta > 0 ? "positive" : preview.ar_delta < 0 ? "danger" : ""} />
                                    <Stat label="AP delta" value={fmtMoney(preview.ap_delta || 0, exposure?.base_currency)} accent={preview.ap_delta > 0 ? "danger" : preview.ap_delta < 0 ? "positive" : ""} />
                                    <Stat label="Items revalued" value={String((preview.detail || []).length)} />
                                </div>
                                <div className="border border-border rounded-md overflow-hidden max-h-[300px] overflow-y-auto">
                                    <table className="fin-table text-[12.5px]">
                                        <thead><tr><th>Type</th><th>Reference</th><th>Currency</th><th className="num">Open Bal</th><th className="num">Book Rate</th><th className="num">New Rate</th><th className="num">Δ Base</th></tr></thead>
                                        <tbody>
                                            {(preview.detail || []).map((d, i) => (
                                                <tr key={i}>
                                                    <td><span className={`text-[10.5px] px-1.5 py-0.5 rounded ${d.type === "AR" ? "bg-[#13192e]/8" : "bg-[#a68a56]/12"}`}>{d.type}</span></td>
                                                    <td className="font-mono">{d.ref}</td>
                                                    <td>{d.currency}</td>
                                                    <td className="num">{fmtMoney(d.balance, d.currency)}</td>
                                                    <td className="num">{d.book_rate.toFixed(4)}</td>
                                                    <td className="num">{d.new_rate.toFixed(4)}</td>
                                                    <td className={`num ${d.delta_base > 0 ? "text-[#1f6c47]" : d.delta_base < 0 ? "text-[#b4233a]" : ""}`}>{fmtMoney(d.delta_base, exposure?.base_currency)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </PageBody>
        </>
    );
}

function Stat({ label, value, accent }) {
    const color = accent === "positive" ? "text-[#1f6c47]" : accent === "danger" ? "text-[#b4233a]" : "text-foreground";
    return (
        <div className="border border-border rounded-md p-3">
            <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
            <div className={`text-base font-semibold mt-0.5 ${color}`}>{value}</div>
        </div>
    );
}
